import { Button, message } from "antd";
import { DownloadOutlined } from "@ant-design/icons";
import * as ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import { getDetalleProductoAll } from "../../../../services/financiero/productosEntregasService";

// 🔥 Formatos numéricos nativos de Excel (celdas tipadas, no texto)
const NUM_FMT = "#,##0.00";
const CURRENCY_FMT = '"$"#,##0.00';
const PERCENT_FMT = "0.00%";

const toNumber = (v) => Number(v) || 0;

export default function ExportExcelButton({ data }) {
  const handleExport = async () => {
    try {
      if (!data || data.length === 0) {
        message.warning("No hay datos para exportar");
        return;
      }

      // 🔥 TRAER TODO EL DETALLE UNA SOLA VEZ
      const allDetalles = await getDetalleProductoAll();

      const workbook = new ExcelJS.Workbook();

      // 🟦 HOJA PRINCIPAL
      const sheet = workbook.addWorksheet("Productos OC");

      // 🟦 HOJA DETALLE
      const detailSheet = workbook.addWorksheet("Detalle");

      // ===============================
      // 🔹 HOJA PRINCIPAL
      // ===============================
      sheet.columns = [
        { header: "PO", key: "Numero_PO", width: 14 },
        { header: "Proyecto", key: "nombre_proyecto", width: 35 },
        { header: "Municipio", key: "municipio", width: 20 },
        { header: "Cantidad", key: "cantidad_total", width: 15, style: { numFmt: NUM_FMT } },
        { header: "Valor Total", key: "valor_total", width: 20, style: { numFmt: CURRENCY_FMT } },
        { header: "Entregado", key: "cantidad_entregada", width: 15, style: { numFmt: NUM_FMT } },
        { header: "Pendiente", key: "pendiente", width: 15, style: { numFmt: NUM_FMT } },
        { header: "% Entrega", key: "porcentaje_entrega", width: 15, style: { numFmt: PERCENT_FMT } },
        { header: "Estado", key: "estado", width: 15 }
      ];

      const headerRow = sheet.getRow(1);
      headerRow.font = { bold: true };
      headerRow.alignment = { horizontal: "center" };

      headerRow.eachCell(cell => {
        cell.fill = {
          type: "pattern",
          pattern: "solid",
          fgColor: { argb: "4472C4" }
        };
        cell.font = { color: { argb: "FFFFFF" }, bold: true };
      });

      data.forEach(p => {
        const porcentaje = toNumber(p.porcentaje_entrega);

        let estado = "Parcial";
        if (porcentaje === 100) estado = "Entregado";
        else if (porcentaje === 0) estado = "Sin entregas";

        sheet.addRow({
          Numero_PO: p.Numero_PO,
          nombre_proyecto: p.nombre_proyecto,
          municipio: p.municipio,
          cantidad_total: toNumber(p.cantidad_total),
          valor_total: toNumber(p.valor_total),
          cantidad_entregada: toNumber(p.cantidad_entregada),
          pendiente: toNumber(p.pendiente),
          porcentaje_entrega: porcentaje / 100,
          estado
        });
      });

      // ===============================
      // 🔹 HOJA DETALLE
      // ===============================
      detailSheet.columns = [
        { header: "PO", key: "Numero_PO", width: 14 },
        { header: "Proyecto", key: "nombre_proyecto", width: 35 },
        { header: "Municipio", key: "municipio", width: 20 },
        { header: "Producto", key: "nombre_producto", width: 40 },
        { header: "Cantidad", key: "cantidad_total", width: 15, style: { numFmt: NUM_FMT } },
        { header: "Entregado", key: "cantidad_entregada", width: 15, style: { numFmt: NUM_FMT } },
        { header: "Pendiente", key: "pendiente", width: 15, style: { numFmt: NUM_FMT } },
        { header: "Valor Unitario", key: "valor_unitario_COP", width: 18, style: { numFmt: CURRENCY_FMT } },
        { header: "Valor Total", key: "valor_total", width: 18, style: { numFmt: CURRENCY_FMT } },
        { header: "% Entrega", key: "porcentaje_entrega", width: 15, style: { numFmt: PERCENT_FMT } }
      ];

      const detailHeader = detailSheet.getRow(1);
      detailHeader.font = { bold: true };
      detailHeader.alignment = { horizontal: "center" };

      detailHeader.eachCell(cell => {
        cell.fill = {
          type: "pattern",
          pattern: "solid",
          fgColor: { argb: "00B0F0" }
        };
        cell.font = { color: { argb: "FFFFFF" }, bold: true };
      });

      // 🔥 LLENAR DETALLE (SIN MÁS REQUESTS)
      data.forEach(p => {
        const detallesFiltrados = allDetalles.filter(d =>
          d.Numero_PO === p.Numero_PO &&
          d.oid_municipio === p.oid_municipio &&
          d.oid_proyecto === p.oid_proyecto &&
          (d.observacion ?? null) === (p.observacion ?? null)
        );

        detallesFiltrados.forEach(d => {
          detailSheet.addRow({
            Numero_PO: p.Numero_PO,
            nombre_proyecto: p.nombre_proyecto,
            municipio: p.municipio,
            nombre_producto: d.nombre_producto,
            cantidad_total: toNumber(d.cantidad_total),
            cantidad_entregada: toNumber(d.cantidad_entregada),
            pendiente: toNumber(d.pendiente),
            valor_unitario_COP: toNumber(d.valor_unitario_COP),
            valor_total: toNumber(d.valor_total),
            porcentaje_entrega: toNumber(d.porcentaje_entrega) / 100
          });
        });
      });

      // ===============================
      // 🔹 AUTO AJUSTE COLUMNAS
      // ===============================
      [sheet, detailSheet].forEach(ws => {
        ws.columns.forEach(col => {
          let maxLength = col.header.length;
          const numFmt = col.style?.numFmt;

          col.eachCell?.({ includeEmpty: true }, cell => {
            if (cell.value === null || cell.value === undefined) return;

            let display;
            if (numFmt === PERCENT_FMT && typeof cell.value === "number") {
              display = `${(cell.value * 100).toFixed(2)}%`;
            } else if (numFmt === CURRENCY_FMT && typeof cell.value === "number") {
              display = `$${cell.value.toLocaleString("es-CO", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
            } else if (numFmt === NUM_FMT && typeof cell.value === "number") {
              display = cell.value.toLocaleString("es-CO", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
            } else {
              display = cell.value.toString();
            }

            if (display.length > maxLength) maxLength = display.length;
          });

          col.width = maxLength + 2;
        });
      });

      // 🔥 DESCARGA
      const buffer = await workbook.xlsx.writeBuffer();

      saveAs(
        new Blob([buffer]),
        `reporte_productos_OC_${new Date().toISOString().split("T")[0]}.xlsx`
      );

      message.success("Excel generado correctamente con detalle");

    } catch (error) {
      console.error(error);
      message.error("Error generando Excel");
    }
  };

  return (
    <Button
      icon={<DownloadOutlined />}
      type="primary"
      onClick={handleExport}
    >
      Exportar Excel
    </Button>
  );
}